import {
  httpGet,
  httpPatch,
  httpPost,
  httpPostFormData,
  httpPut,
  httpDelete,
} from "@/lib/http";
import { API_ENDPOINTS } from "@/lib/constants/api-endpoints";
import type { PaginatedResponse } from "@/lib/types/http.types";
import type {
  Submission,
  SubmissionUpdatePayload,
  GetSubmissionsParams,
  SubmissionsResponse,
  ScheduleOfHazard,
  CoverLetter,
  CoverLetterRevision,
  CommunicationReviewPage,
  ConversationReviewMessage,
  CommunicationsSummary,
  ConversationThread,
  ConversationThreadMessage,
  ConversationMessageEmail,
  ConversationReplyAttachmentRef,
  MarketFinderUserPreferences,
  MarketFinderUserPreferencesUpdate,
  AimCompanyPage,
  AimContactImportInput,
  AimContactImportResult,
  AimContactPage,
  AimMarketImportInput,
  MarketFinderActorInfo,
  MarketFinderDemoAgentThreadResult,
  MarketFinderDemoConfigResult,
  MarketFinderDemoResetResult,
  MarketContact,
  MarketContactDirectoryParams,
  MarketContactDirectoryResponse,
  MarketContactInput,
  MarketCreateInput,
  MarketCreateResponse,
  MarketDirectoryItem,
  MarketsDirectoryParams,
  MarketsDirectoryResponse,
  MarketUpdateInput,
  MarketPacket,
  PossibleMatch,
  MarketPacketPreview,
  MarketPacketSendResult,
  MarketPacketUpdateInput,
  AgentEmailInput,
  PremiseData,
  BuildingData,
  BuildingPayload,
} from "../types/submissions.types";
import type { AuthHeaders } from "@/lib/auth/types";
import type { HitRateResponse } from "../types/hit-rates.types";
import type {
  ClassCodeInput,
  GLCoverageInput,
} from "../validators/general-liability.validator";

export interface SubmissionEndpointConfig {
  list: string;
  detail: (id: number) => string;
  archive: (id: number) => string;
  underwriters?: string;
  generalLiability: (id: number) => string;
  premises: {
    list: (id: number) => string;
    detail: (id: number, premiseId: number) => string;
  };
  buildings: {
    list: (id: number, premiseId: number) => string;
    detail: (id: number, buildingId: number) => string;
  };
}

const EMAIL_SEND_TIMEOUT = 300000;

const DEFAULT_ENDPOINTS: SubmissionEndpointConfig = {
  list: API_ENDPOINTS.submissions.list,
  detail: API_ENDPOINTS.submissions.detail,
  archive: API_ENDPOINTS.submissions.archive,
  underwriters: API_ENDPOINTS.submissions.underwriters,
  generalLiability: API_ENDPOINTS.generalLiability.coverage,
  premises: API_ENDPOINTS.premises,
  buildings: API_ENDPOINTS.buildings,
};

export class SubmissionsRepository {
  private endpoints: SubmissionEndpointConfig;

  constructor(endpoints?: SubmissionEndpointConfig) {
    this.endpoints = endpoints || DEFAULT_ENDPOINTS;
  }

  async getSubmissions(
    params: GetSubmissionsParams,
  ): Promise<SubmissionsResponse> {
    const queryParams: Record<string, number | string> = {
      page: params.page,
      size: params.per_page,
    };

    if (params.show_archived !== undefined) {
      queryParams.show_archived = String(params.show_archived);
    }

    if (params.sort?.column && params.sort?.direction) {
      queryParams.sort_column = params.sort.column;
      queryParams.sort_direction = params.sort.direction;
    }

    if (params.filters && params.filters.length > 0) {
      queryParams.filters = JSON.stringify(params.filters);
    }

    if (params.search) {
      queryParams.search = params.search;
    }

    if (params.office_ids && params.office_ids.length > 0) {
      queryParams.office_ids = params.office_ids.join(",");
    }

    if (params.company_ids && params.company_ids.length > 0) {
      queryParams.company_ids = params.company_ids.join(",");
    }

    return httpGet<PaginatedResponse<Submission>>(this.endpoints.list, {
      params: queryParams,
      headers: params.headers,
    });
  }

  async getDistinctUnderwriters(params: {
    office_ids?: number[];
    company_ids?: number[];
    show_archived?: boolean;
    headers: AuthHeaders;
  }): Promise<{ id: string; displayName: string; self?: boolean }[]> {
    const endpoint = this.endpoints.underwriters;
    if (!endpoint) return [];
    const queryParams: Record<string, string> = {};
    if (params.office_ids?.length) {
      queryParams.office_ids = params.office_ids.join(",");
    }
    if (params.company_ids?.length) {
      queryParams.company_ids = params.company_ids.join(",");
    }
    if (params.show_archived) {
      queryParams.show_archived = "true";
    }
    return httpGet<{ id: string; displayName: string; self?: boolean }[]>(endpoint, {
      params: queryParams,
      headers: params.headers,
    });
  }

  async batchAssign(
    applicationIds: number[],
    companyId: number,
    officeId: number | null,
    headers: AuthHeaders,
  ): Promise<{ updated_count: number }> {
    return httpPatch<{ updated_count: number }>(
      API_ENDPOINTS.submissions.batchAssign,
      {
        application_ids: applicationIds,
        company_id: companyId,
        office_id: officeId,
      },
      { headers },
    );
  }

  async getSubmissionById(
    id: number,
    headers: AuthHeaders,
  ): Promise<Submission> {
    return httpGet<Submission>(this.endpoints.detail(id), { headers });
  }

  async archiveSubmission(
    id: number,
    archive: boolean,
    headers: AuthHeaders,
  ): Promise<void> {
    await httpPatch(
      this.endpoints.archive(id),
      {
        is_active: !archive,
      },
      { headers },
    );
  }

  async unarchiveSubmission(
    id: number,
    headers: AuthHeaders,
  ): Promise<void> {
    await this.archiveSubmission(id, false, headers);
  }

  async addClassCode(
    submissionId: number,
    data: ClassCodeInput,
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard> {
    const payload = {
      class_code: data.class_code,
      classification: data.description,
      exposure: data.payroll || 0,
      premium_basis: "Payroll",
      exposure_unit: "per $1000",
    };

    return httpPost<ScheduleOfHazard>(
      API_ENDPOINTS.generalLiability.scheduleOfHazard(submissionId),
      payload,
      { headers },
    );
  }

  async updateGeneralLiabilitySchedule(
    submissionId: number,
    scheduleOfHazard: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<{ schedule_of_hazard: ScheduleOfHazard[] }> {
    return httpPatch<{ schedule_of_hazard: ScheduleOfHazard[] }>(
      this.endpoints.generalLiability(submissionId),
      { schedule_of_hazard: scheduleOfHazard },
      { headers },
    );
  }

  async updateClassCode(
    submissionId: number,
    classCodeId: number,
    data: ClassCodeInput,
    allCodes: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<{ schedule_of_hazard: ScheduleOfHazard[] }> {
    const nextIfAny = data.if_any ?? false;
    // When If Any is on, the percentage is meaningless — force 0 regardless
    // of what the caller passed (or didn't pass).
    const nextPercentage = nextIfAny
      ? 0
      : data.percentage_of_operations !== undefined
        ? data.percentage_of_operations
        : null;
    const updatedCodes = allCodes.map((code) =>
      code.id === classCodeId
        ? {
            ...code,
            class_code: data.class_code,
            classification: data.description,
            exposure: data.payroll || 0,
            premium_basis: data.premium_basis || "P",
            if_any: nextIfAny,
            percentage_of_operations: nextPercentage,
            inferred_fields: [],
          }
        : code,
    );

    // PATCH with complete array (multi-rater pattern)
    return httpPatch<{ schedule_of_hazard: ScheduleOfHazard[] }>(
      this.endpoints.generalLiability(submissionId),
      { schedule_of_hazard: updatedCodes },
      { headers },
    );
  }

  async deleteClassCode(
    submissionId: number,
    remainingCodes: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<{ schedule_of_hazard: ScheduleOfHazard[] }> {
    return httpPatch<{ schedule_of_hazard: ScheduleOfHazard[] }>(
      this.endpoints.generalLiability(submissionId),
      { schedule_of_hazard: remainingCodes },
      { headers },
    );
  }

  async updateSubmission(
    submissionId: number,
    data: SubmissionUpdatePayload,
    headers: AuthHeaders,
  ): Promise<Submission> {
    return httpPatch<Submission>(this.endpoints.detail(submissionId), data, {
      headers,
    });
  }

  async updateGLPartial(
    submissionId: number,
    data: Record<string, unknown>,
    headers: AuthHeaders,
  ): Promise<void> {
    await httpPatch(this.endpoints.generalLiability(submissionId), data, {
      headers,
    });
  }

  async updateGLCoverage(
    submissionId: number,
    data: GLCoverageInput & { inferred_fields?: unknown[] },
    headers: AuthHeaders,
  ): Promise<void> {
    await httpPatch(
      this.endpoints.generalLiability(submissionId),
      {
        each_occurrence: parseInt(data.each_occurrence, 10),
        general_aggregate_limit: parseInt(data.general_aggregate_limit, 10),
        products_completed_operations_aggregate: parseInt(
          data.products_completed_operations_aggregate,
          10,
        ),
        personal_advertising_injury: parseInt(
          data.personal_advertising_injury,
          10,
        ),
        damage_to_rented_premises: parseInt(data.damage_to_rented_premises, 10),
        medical_expense: parseInt(data.medical_expense, 10),
        employee_benefits: data.employee_benefits,
        deductible_type: data.deductible_type ?? "",
        deductible_amount: parseInt(data.deductible_amount, 10),
        inferred_fields: data.inferred_fields ?? [],
      },
      { headers },
    );
  }

  async getAllClassCodes(): Promise<ClassCodeSearchResult[]> {
    const response = await httpGet<
      Record<string, { description: string; premium_unit?: string | null }>
    >(API_ENDPOINTS.classCodes.all);

    return Object.entries(response).map(([code, data]) => ({
      ClassCode: code,
      ClassDescription: data.description,
      PremiumUnit: data.premium_unit ?? null,
      EGuideLink: "",
      score: 1,
      match_type: "exact",
    }));
  }

  async getAllPropertyClassCodes(): Promise<
    Array<{ code: string; description: string }>
  > {
    const response = await httpGet<
      Record<string, { description: string }>
    >(API_ENDPOINTS.classCodes.property);

    return Object.entries(response).map(([code, data]) => ({
      code,
      description: data.description,
    }));
  }

  async searchClassCodes(
    query: string,
    limit: number = 50,
  ): Promise<ClassCodeSearchResult[]> {
    const params = { query, limit: String(limit) };
    return httpGet<ClassCodeSearchResult[]>(
      API_ENDPOINTS.submissions.classCodesSearch,
      {
        params,
      },
    );
  }

  async createPremise(
    submissionId: number,
    data: { full_address: string; description_of_operations?: string },
    headers: AuthHeaders,
  ): Promise<PremiseData> {
    return httpPost<PremiseData>(
      this.endpoints.premises.list(submissionId),
      data,
      { headers },
    );
  }

  async updatePremise(
    submissionId: number,
    premiseId: number,
    data: {
      full_address?: string;
      description_of_operations?: string;
      nature_of_business?: string;
      location_number?: number;
    },
    headers: AuthHeaders,
  ): Promise<PremiseData> {
    return httpPatch<PremiseData>(
      this.endpoints.premises.detail(submissionId, premiseId),
      data,
      { headers },
    );
  }

  async deletePremise(
    submissionId: number,
    premiseId: number,
    headers: AuthHeaders,
  ): Promise<void> {
    await httpDelete(
      this.endpoints.premises.detail(submissionId, premiseId),
      { headers },
    );
  }

  async createBuilding(
    submissionId: number,
    premiseId: number,
    data: BuildingPayload,
    headers: AuthHeaders,
  ): Promise<BuildingData> {
    return httpPost<BuildingData>(
      this.endpoints.buildings.list(submissionId, premiseId),
      data,
      { headers },
    );
  }

  async updateBuilding(
    submissionId: number,
    buildingId: number,
    data: BuildingPayload,
    headers: AuthHeaders,
  ): Promise<BuildingData> {
    return httpPatch<BuildingData>(
      this.endpoints.buildings.detail(submissionId, buildingId),
      data,
      { headers },
    );
  }

  async deleteBuilding(
    submissionId: number,
    buildingId: number,
    headers: AuthHeaders,
  ): Promise<void> {
    await httpDelete(
      this.endpoints.buildings.detail(submissionId, buildingId),
      { headers },
    );
  }

  // Cover letter methods

  async generateCoverLetter(
    appId: number,
    data: {
      carrier_id?: string;
      carrier_name?: string;
      recipient_email?: string;
    },
  ): Promise<CoverLetter> {
    return httpPost<CoverLetter>(
      API_ENDPOINTS.marketFinder.coverLetterGenerate(appId),
      data,
    );
  }

  async getCoverLetters(appId: number): Promise<CoverLetter[]> {
    return httpGet<CoverLetter[]>(API_ENDPOINTS.marketFinder.coverLetters(appId));
  }

  async getCoverLetter(appId: number, clId: number): Promise<CoverLetter> {
    return httpGet<CoverLetter>(
      API_ENDPOINTS.marketFinder.coverLetterDetail(appId, clId),
    );
  }

  async updateCoverLetter(
    appId: number,
    clId: number,
    data: {
      subject?: string;
      body_html?: string;
      recipient_email?: string;
      revision_source?: "AI_ASSIST" | "MANUAL";
      revision_id?: number;
      action?: "improve" | "formal" | "concise" | "expand";
    },
  ): Promise<CoverLetter> {
    return httpPatch<CoverLetter>(
      API_ENDPOINTS.marketFinder.coverLetterDetail(appId, clId),
      data,
    );
  }

  async aiAssist(data: {
    text: string;
    action: "improve" | "formal" | "concise" | "expand";
    app_id?: number;
    cover_letter_id?: number;
  }): Promise<{ text: string }> {
    return httpPost<{ text: string }>(API_ENDPOINTS.marketFinder.aiAssist, data);
  }

  async getMarketFinderUserPreferences(): Promise<MarketFinderUserPreferences> {
    return httpGet<MarketFinderUserPreferences>(
      API_ENDPOINTS.marketFinder.userPreferences,
    );
  }

  async updateMarketFinderUserPreferences(
    data: MarketFinderUserPreferencesUpdate,
  ): Promise<MarketFinderUserPreferences> {
    return httpPatch<MarketFinderUserPreferences>(
      API_ENDPOINTS.marketFinder.userPreferences,
      data,
    );
  }

  async getHitRates(classCodes: string[]): Promise<HitRateResponse> {
    return httpGet<HitRateResponse>(
      API_ENDPOINTS.marketFinder.hitRates(classCodes),
    );
  }

  async getPackets(appId: number): Promise<MarketPacket[]> {
    return httpGet<MarketPacket[]>(API_ENDPOINTS.marketFinder.packets(appId));
  }

  async createPacket(
    appId: number,
    data: { label?: string; lob: string[] },
  ): Promise<MarketPacket> {
    return httpPost<MarketPacket>(API_ENDPOINTS.marketFinder.packets(appId), data);
  }

  async updatePacket(
    appId: number,
    packetId: number,
    data: MarketPacketUpdateInput,
  ): Promise<MarketPacket> {
    return httpPatch<MarketPacket>(
      API_ENDPOINTS.marketFinder.packetDetail(appId, packetId),
      data,
    );
  }

  async deletePacket(appId: number, packetId: number): Promise<void> {
    await httpDelete(API_ENDPOINTS.marketFinder.packetDetail(appId, packetId));
  }

  async setPacketAttachments(
    appId: number,
    packetId: number,
    recordIds: number[],
  ): Promise<MarketPacket> {
    return httpPut<MarketPacket>(
      API_ENDPOINTS.marketFinder.packetAttachments(appId, packetId),
      { record_ids: recordIds },
    );
  }

  async setPacketRecipients(
    appId: number,
    packetId: number,
    recipients: Array<{ carrier_id: string; contact_id: number }>,
  ): Promise<MarketPacket> {
    return httpPut<MarketPacket>(
      API_ENDPOINTS.marketFinder.packetRecipients(appId, packetId),
      { recipients },
    );
  }

  async generatePacketCoverLetter(
    appId: number,
    packetId: number,
    data: {
      current_body_html?: string;
      format?: "standard" | "concise" | "detailed";
    } = {},
  ): Promise<CoverLetter> {
    return httpPost<CoverLetter>(
      API_ENDPOINTS.marketFinder.packetCoverLetterGenerate(appId, packetId),
      data,
    );
  }

  async updatePacketCoverLetter(
    appId: number,
    packetId: number,
    data: {
      subject?: string;
      body_html?: string;
      revision_source?: "AI_ASSIST" | "MANUAL";
      revision_id?: number;
      action?: string;
    },
  ): Promise<CoverLetter> {
    return httpPatch<CoverLetter>(
      API_ENDPOINTS.marketFinder.packetCoverLetter(appId, packetId),
      data,
    );
  }

  async createNewPacketCoverLetter(
    appId: number,
    packetId: number,
  ): Promise<CoverLetter> {
    return httpPost<CoverLetter>(
      API_ENDPOINTS.marketFinder.packetCoverLetterNew(appId, packetId),
      {},
    );
  }

  async getPacketCoverLetterRevisions(
    appId: number,
    packetId: number,
  ): Promise<CoverLetterRevision[]> {
    return httpGet<CoverLetterRevision[]>(
      API_ENDPOINTS.marketFinder.packetCoverLetterRevisions(appId, packetId),
    );
  }

  async previewPacket(appId: number, packetId: number): Promise<MarketPacketPreview> {
    return httpPost<MarketPacketPreview>(
      API_ENDPOINTS.marketFinder.packetPreview(appId, packetId),
      {},
    );
  }

  async sendPacket(appId: number, packetId: number): Promise<MarketPacketSendResult> {
    return httpPost<MarketPacketSendResult>(
      API_ENDPOINTS.marketFinder.packetSend(appId, packetId),
      {},
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async sendPacketAgentEmail(
    appId: number,
    packetId: number,
    data: AgentEmailInput,
  ): Promise<ConversationThread> {
    return httpPost<ConversationThread>(
      API_ENDPOINTS.marketFinder.packetAgentConversations(appId, packetId),
      data,
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async uploadPacketAgentEmailAttachments(
    appId: number,
    packetId: number,
    formData: FormData,
  ): Promise<ConversationReplyAttachmentRef[]> {
    return httpPostFormData<ConversationReplyAttachmentRef[]>(
      API_ENDPOINTS.marketFinder.packetAgentConversationAttachments(appId, packetId),
      formData,
    );
  }

  async getCommunicationsSummary(
    underwriterIds?: string[],
  ): Promise<CommunicationsSummary> {
    const suffix = underwriterIds?.length
      ? `?underwriter_ids=${encodeURIComponent(underwriterIds.join(","))}`
      : "";
    return httpGet<CommunicationsSummary>(
      `${API_ENDPOINTS.marketFinder.communicationsSummary}${suffix}`,
    );
  }

  async listApplicationConversations(
    appId: number,
  ): Promise<ConversationThread[]> {
    return httpGet<ConversationThread[]>(
      API_ENDPOINTS.marketFinder.conversations(appId),
    );
  }

  async listConversationMessages(
    appId: number,
    conversationId: number,
  ): Promise<ConversationThreadMessage[]> {
    return httpGet<ConversationThreadMessage[]>(
      API_ENDPOINTS.marketFinder.conversationMessages(appId, conversationId),
    );
  }

  async getConversationMessageEmail(
    appId: number,
    messageId: number,
  ): Promise<ConversationMessageEmail> {
    return httpGet<ConversationMessageEmail>(
      API_ENDPOINTS.marketFinder.conversationMessageEmail(appId, messageId),
    );
  }

  async listPossibleMatches(appId: number): Promise<PossibleMatch[]> {
    return httpGet<PossibleMatch[]>(
      API_ENDPOINTS.marketFinder.possibleMatches(appId),
    );
  }

  async acceptPossibleMatch(
    appId: number,
    messageId: number,
    conversationId: number,
  ): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.possibleMatchAccept(appId, messageId),
      { conversation_id: conversationId },
    );
  }

  async rejectPossibleMatch(appId: number, messageId: number): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.possibleMatchReject(appId, messageId),
      {},
    );
  }

  async markConversationMessageRead(
    appId: number,
    messageId: number,
  ): Promise<ConversationThreadMessage> {
    return httpPost<ConversationThreadMessage>(
      API_ENDPOINTS.marketFinder.conversationMessageRead(appId, messageId),
      {},
    );
  }

  async detachEmailFromConversation(
    appId: number,
    messageId: number,
  ): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.conversationEmailDetach(appId, messageId),
      {},
    );
  }

  async attachEmailToConversation(
    appId: number,
    messageId: number,
    conversationId: number,
  ): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.conversationEmailAttach(appId, messageId),
      { conversation_id: conversationId },
    );
  }

  async replyToConversation(
    appId: number,
    conversationId: number,
    bodyHtml: string,
    attachments: ConversationReplyAttachmentRef[] = [],
    targetMessageId?: number,
  ): Promise<ConversationThreadMessage> {
    return httpPost<ConversationThreadMessage>(
      API_ENDPOINTS.marketFinder.conversationReply(appId, conversationId),
      {
        body_html: bodyHtml,
        attachments,
        ...(targetMessageId == null
          ? {}
          : { target_message_id: targetMessageId }),
      },
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async uploadConversationReplyAttachments(
    appId: number,
    conversationId: number,
    formData: FormData,
  ): Promise<ConversationReplyAttachmentRef[]> {
    return httpPostFormData<ConversationReplyAttachmentRef[]>(
      API_ENDPOINTS.marketFinder.conversationReplyAttachments(appId, conversationId),
      formData,
    );
  }

  async listCommunicationReviewMessages(options: {
    archived?: boolean;
    page?: number;
    perPage?: number;
    search?: string;
    filters?: string;
  }): Promise<CommunicationReviewPage> {
    const params: Record<string, string> = {
      archived: String(options.archived ?? false),
      page: String(options.page ?? 1),
      per_page: String(options.perPage ?? 25),
    };
    if (options.search) params.search = options.search;
    if (options.filters) params.filters = options.filters;
    return httpGet<CommunicationReviewPage>(
      API_ENDPOINTS.marketFinder.communicationReview,
      { params },
    );
  }

  async getCommunicationReviewMessage(
    messageId: number,
  ): Promise<ConversationReviewMessage> {
    return httpGet<ConversationReviewMessage>(
      API_ENDPOINTS.marketFinder.communicationReviewMessage(messageId),
    );
  }

  async resolveCommunicationReviewMessage(
    messageId: number,
    conversationId: number,
  ): Promise<ConversationReviewMessage> {
    return httpPost<ConversationReviewMessage>(
      API_ENDPOINTS.marketFinder.communicationReviewResolve(messageId),
      { conversation_id: conversationId },
    );
  }

  async resolveCommunicationReviewMessageByReference(
    messageId: number,
    conversationReference: string,
  ): Promise<ConversationReviewMessage> {
    return httpPost<ConversationReviewMessage>(
      API_ENDPOINTS.marketFinder.communicationReviewResolve(messageId),
      { conversation_reference: conversationReference },
    );
  }

  async dismissCommunicationReviewMessage(
    messageId: number,
  ): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.communicationReviewDismiss(messageId),
      {},
    );
  }

  async archiveCommunicationReviewMessage(
    messageId: number,
  ): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.communicationReviewArchive(messageId),
      {},
    );
  }

  async unarchiveCommunicationReviewMessage(
    messageId: number,
  ): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.communicationReviewUnarchive(messageId),
      {},
    );
  }

  async getCommunicationReviewMessageEmail(
    messageId: number,
  ): Promise<ConversationMessageEmail> {
    return httpGet<ConversationMessageEmail>(
      API_ENDPOINTS.marketFinder.communicationReviewEmail(messageId),
    );
  }

  async getMarkets(
    lob?: string,
    includeInactive?: boolean,
  ): Promise<MarketDirectoryItem[]> {
    return httpGet<MarketDirectoryItem[]>(
      API_ENDPOINTS.marketFinder.markets(lob, includeInactive),
    );
  }

  async getMarketsDirectory(
    params: MarketsDirectoryParams,
  ): Promise<MarketsDirectoryResponse> {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page));
    qs.set("size", String(params.size));
    if (params.search) qs.set("search", params.search);
    if (params.show_archived) qs.set("show_archived", "true");
    if (params.market_type) qs.set("market_type", params.market_type);
    if (params.source) qs.set("source", params.source);
    if (params.lob) qs.set("lob", params.lob);
    if (params.sort_column) {
      qs.set("sort_column", params.sort_column);
      qs.set("sort_direction", params.sort_direction ?? "asc");
    }
    return httpGet<MarketsDirectoryResponse>(
      `${API_ENDPOINTS.marketFinder.marketsDirectory}?${qs.toString()}`,
    );
  }

  async setContactActive(
    contactId: number,
    archive: boolean,
  ): Promise<MarketContact> {
    return httpPost<MarketContact>(
      API_ENDPOINTS.marketFinder.marketContactArchive(contactId, archive),
      {},
    );
  }

  async updateMarket(
    carrierId: string,
    data: MarketUpdateInput,
  ): Promise<MarketDirectoryItem> {
    return httpPatch<MarketDirectoryItem>(
      API_ENDPOINTS.marketFinder.marketUpdate(carrierId),
      data,
    );
  }

  async uploadMarketImage(
    carrierId: string,
    file: File,
  ): Promise<MarketDirectoryItem> {
    const formData = new FormData();
    formData.append("file", file);
    return httpPostFormData<MarketDirectoryItem>(
      API_ENDPOINTS.marketFinder.marketImage(carrierId),
      formData,
    );
  }

  async deleteMarketImage(carrierId: string): Promise<MarketDirectoryItem> {
    return httpDelete<MarketDirectoryItem>(
      API_ENDPOINTS.marketFinder.marketImage(carrierId),
    );
  }

  async createMarket(data: MarketCreateInput): Promise<MarketCreateResponse> {
    return httpPost<MarketCreateResponse>(
      API_ENDPOINTS.marketFinder.marketCreate,
      data,
    );
  }

  async getMarketContacts(carrierId: string): Promise<MarketContact[]> {
    return httpGet<MarketContact[]>(
      API_ENDPOINTS.marketFinder.marketContacts(carrierId),
    );
  }

  async getMarketContactsDirectory(
    params: MarketContactDirectoryParams,
  ): Promise<MarketContactDirectoryResponse> {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page));
    qs.set("size", String(params.size));
    if (params.search) qs.set("search", params.search);
    if (params.scope) qs.set("scope", params.scope);
    if (params.source) qs.set("source", params.source);
    if (params.show_archived) qs.set("show_archived", "true");
    if (params.sort_column) {
      qs.set("sort_column", params.sort_column);
      qs.set("sort_direction", params.sort_direction ?? "asc");
    }
    return httpGet<MarketContactDirectoryResponse>(
      `${API_ENDPOINTS.marketFinder.marketContactsDirectory}?${qs.toString()}`,
    );
  }

  async createMarketContact(
    carrierId: string,
    data: MarketContactInput,
  ): Promise<MarketContact> {
    return httpPost<MarketContact>(
      API_ENDPOINTS.marketFinder.marketContacts(carrierId),
      data,
    );
  }

  async updateMarketContact(
    contactId: number,
    data: MarketContactInput,
  ): Promise<MarketContact> {
    return httpPatch<MarketContact>(
      API_ENDPOINTS.marketFinder.marketContact(contactId),
      data,
    );
  }

  async getMarketFinderMe(): Promise<MarketFinderActorInfo> {
    return httpGet<MarketFinderActorInfo>(API_ENDPOINTS.marketFinder.me);
  }

  async getMarketFinderDemoConfig(): Promise<MarketFinderDemoConfigResult> {
    return httpGet<MarketFinderDemoConfigResult>(
      API_ENDPOINTS.marketFinder.demoConfig,
    );
  }

  async sendMarketFinderDemoAgentMessage(
    conversationId: number,
    bodyHtml: string,
  ): Promise<ConversationThreadMessage> {
    return httpPost<ConversationThreadMessage>(
      API_ENDPOINTS.marketFinder.demoAgentMessage(conversationId),
      { body_html: bodyHtml },
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async seedMarketFinderDemoAgentThread(
    packetId: number,
  ): Promise<MarketFinderDemoAgentThreadResult> {
    return httpPost<MarketFinderDemoAgentThreadResult>(
      API_ENDPOINTS.marketFinder.demoAgentThread(packetId),
      {},
    );
  }

  async resetMarketFinderDemo(): Promise<MarketFinderDemoResetResult> {
    return httpPost<MarketFinderDemoResetResult>(
      API_ENDPOINTS.marketFinder.demoReset,
      {},
    );
  }

  async searchAimCompanies(
    q: string,
    page = 0,
    size = 20,
  ): Promise<AimCompanyPage> {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    qs.set("page", String(page));
    qs.set("size", String(size));
    return httpGet<AimCompanyPage>(
      `${API_ENDPOINTS.marketFinder.aimCompanies}?${qs.toString()}`,
    );
  }

  async getAimCompanyContacts(
    referenceId: number,
    q = "",
    page = 0,
    size = 20,
    carrierId?: string,
  ): Promise<AimContactPage> {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    qs.set("page", String(page));
    qs.set("size", String(size));
    // Already-imported flags are carrier-specific — same AIM contact can be
    // imported under several markets.
    if (carrierId) qs.set("carrier_id", carrierId);
    return httpGet<AimContactPage>(
      `${API_ENDPOINTS.marketFinder.aimCompanyContacts(referenceId)}?${qs.toString()}`,
    );
  }

  async importAimMarket(data: AimMarketImportInput): Promise<MarketCreateResponse> {
    return httpPost<MarketCreateResponse>(
      API_ENDPOINTS.marketFinder.marketAimImport,
      data,
    );
  }

  async importAimContacts(
    carrierId: string,
    data: AimContactImportInput,
  ): Promise<AimContactImportResult> {
    return httpPost<AimContactImportResult>(
      API_ENDPOINTS.marketFinder.marketContactsAimImport(carrierId),
      data,
    );
  }
}

export interface ClassCodeSearchResult {
  ClassCode: string;
  ClassDescription: string;
  PremiumUnit?: string | null;
  EGuideLink: string;
  score: number;
  match_type: string;
}

export function createSubmissionsRepository(): SubmissionsRepository {
  return new SubmissionsRepository();
}

export function createMarketFinderRepository(): SubmissionsRepository {
  return new SubmissionsRepository({
    list: API_ENDPOINTS.marketFinder.list,
    detail: API_ENDPOINTS.marketFinder.detail,
    archive: API_ENDPOINTS.marketFinder.archive,
    underwriters: API_ENDPOINTS.marketFinder.underwriters,
    generalLiability: API_ENDPOINTS.marketFinder.generalLiability,
    premises: API_ENDPOINTS.marketFinder.premises,
    buildings: API_ENDPOINTS.marketFinder.buildings,
  });
}
